import { useCallback, useState } from "react";
import { Check, CheckCheck, Download, Maximize2, Paperclip, Trash2 } from "lucide-react";
import { downloadMessageAttachment, getMessageAttachmentMeta } from "./messagingUi";

function formatTime(dateStr) {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function ReadIcon({ status }) {
  if (status === "READ") return <CheckCheck size={11} className="text-emerald-400" />;
  if (status === "DELIVERED") return <CheckCheck size={11} className="text-neutral-400" />;
  return <Check size={11} className="text-neutral-400" />;
}

function AttachmentActionButton({
  icon: Icon,
  label,
  onClick,
  disabled = false,
  variant = "default",
}) {
  const variantClassName = variant === "subtle"
    ? "text-neutral-400 hover:text-neutral-200"
    : "text-inherit hover:opacity-80";

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-1 transition disabled:cursor-not-allowed disabled:opacity-50 ${variantClassName}`}
    >
      <Icon size={12} />
      <span>{label}</span>
    </button>
  );
}

export default function MessageBubble({
  message,
  isMine,
  actorType = "SHOPPER",
  onPreviewAttachment,
  canDelete = false,
  pendingDelete = false,
  onDelete,
}) {
  const attachment = getMessageAttachmentMeta(message, actorType);
  const [downloadingAttachment, setDownloadingAttachment] = useState(false);
  const [downloadError, setDownloadError] = useState("");

  const handleDownloadAttachment = useCallback(async (event) => {
    event.stopPropagation();
    if (!attachment.hasAttachment || downloadingAttachment) return;

    setDownloadingAttachment(true);
    setDownloadError("");
    try {
      await downloadMessageAttachment(attachment);
    } catch (error) {
      setDownloadError(
        error?.message || "Unable to download this attachment right now.",
      );
    } finally {
      setDownloadingAttachment(false);
    }
  }, [attachment, downloadingAttachment]);

  return (
    <div className={`group flex items-end gap-2 py-0.5 ${isMine ? "justify-end" : "justify-start"}`}>
      <div
        className={`relative max-w-[85%] min-w-0 rounded-2xl px-3.5 py-2.5 text-[13px] leading-relaxed shadow-sm ring-1 ring-black/5 [overflow-wrap:anywhere] sm:max-w-[78%] ${
          isMine
            ? "rounded-br-sm bg-neutral-900 text-white dark:bg-neutral-100 dark:text-neutral-900 dark:ring-white/10"
            : "rounded-bl-sm bg-neutral-100 text-neutral-800 dark:bg-neutral-800 dark:text-neutral-100 dark:ring-white/10"
        }`}
      >
        {/* Attachment */}
        {attachment.hasAttachment && (
          <div className="mb-2">
            {attachment.kind === "image" ? (
              <div className="overflow-hidden rounded-2xl border border-black/10 dark:border-white/10">
                <button
                  type="button"
                  onClick={() => onPreviewAttachment?.(attachment)}
                  className="block max-w-full overflow-hidden"
                  aria-label={`Preview ${attachment.label}`}
                >
                  <img
                    src={attachment.url}
                    alt={attachment.label}
                    className="max-h-[18rem] w-full max-w-[min(18rem,72vw)] object-cover transition hover:opacity-90 sm:max-w-[20rem]"
                  />
                </button>
              </div>
            ) : attachment.kind === "video" ? (
              <div className="overflow-hidden rounded-2xl border border-black/10 bg-black/80 dark:border-white/10">
                <video
                  src={attachment.url}
                  controls
                  playsInline
                  className="max-h-[18rem] w-full max-w-[min(18rem,72vw)] bg-black object-contain sm:max-w-[20rem]"
                  preload="metadata"
                />
                <div className={`flex items-center justify-between gap-2 px-3 py-2 text-[11px] ${
                  isMine ? "text-neutral-200" : "text-neutral-300"
                }`}>
                  <span className="min-w-0 truncate">{attachment.label}</span>
                  <div className="flex shrink-0 items-center gap-1">
                    <AttachmentActionButton
                      icon={Maximize2}
                      label="Expand"
                      onClick={() => onPreviewAttachment?.(attachment)}
                      variant="subtle"
                    />
                    <AttachmentActionButton
                      icon={Download}
                      label={downloadingAttachment ? "Preparing..." : "Download"}
                      onClick={handleDownloadAttachment}
                      disabled={downloadingAttachment}
                      variant="subtle"
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div
                className={`flex w-full max-w-full items-center gap-3 rounded-2xl border px-3 py-2.5 text-left text-[11px] transition ${
                  isMine
                    ? "border-white/10 bg-white/10 text-blue-100 hover:bg-white/15 dark:border-neutral-300/20 dark:bg-neutral-900/20 dark:text-blue-600"
                    : "border-neutral-200 bg-white text-blue-600 hover:bg-neutral-50 dark:border-neutral-700 dark:bg-neutral-900/40 dark:text-blue-400"
                }`}
              >
                <button
                  type="button"
                  onClick={() => onPreviewAttachment?.(attachment)}
                  className="flex min-w-0 flex-1 items-center gap-3 text-left"
                  aria-label={`Preview ${attachment.label}`}
                >
                  <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${
                    isMine ? "bg-white/10" : "bg-neutral-100 dark:bg-neutral-800"
                  }`}>
                    <Paperclip size={14} className="shrink-0" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <span className="block truncate font-medium">{attachment.label}</span>
                    {attachment.sizeLabel ? (
                      <span className={`mt-0.5 block truncate ${isMine ? "text-neutral-300 dark:text-neutral-500" : "text-neutral-400"}`}>
                        {attachment.sizeLabel}
                      </span>
                    ) : null}
                  </div>
                </button>
                <div className="flex shrink-0 items-center gap-1">
                  <AttachmentActionButton
                    icon={Maximize2}
                    label="Preview"
                    onClick={() => onPreviewAttachment?.(attachment)}
                  />
                  <AttachmentActionButton
                    icon={Download}
                    label={downloadingAttachment ? "Preparing..." : "Download"}
                    onClick={handleDownloadAttachment}
                    disabled={downloadingAttachment}
                  />
                </div>
              </div>
            )}
            {downloadError ? (
              <p className={`mt-2 text-[10px] ${
                isMine ? "text-rose-200 dark:text-rose-500" : "text-rose-600 dark:text-rose-300"
              }`}>
                {downloadError}
              </p>
            ) : null}
          </div>
        )}

        {/* Text */}
        {message.message_text && <p className="whitespace-pre-wrap break-words">{message.message_text}</p>}

        {/* Meta: time + read status */}
        <div className={`mt-2 flex items-center gap-1.5 ${isMine ? "justify-end" : ""}`}>
          {isMine && canDelete && typeof onDelete === "function" && (
            <button
              type="button"
              onClick={() => onDelete(message.id)}
              disabled={pendingDelete}
              className={`inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-[10px] transition ${
                isMine
                  ? "text-neutral-300 hover:bg-white/10 hover:text-white dark:text-neutral-500 dark:hover:bg-neutral-900/20"
                  : "text-neutral-400 hover:bg-neutral-200 hover:text-neutral-700 dark:hover:bg-neutral-700"
              } disabled:opacity-50`}
              aria-label="Delete message"
            >
              <Trash2 size={10} />
              <span>{pendingDelete ? "Deleting" : "Delete"}</span>
            </button>
          )}
          <span className={`text-[10px] ${isMine ? "text-neutral-300 dark:text-neutral-500" : "text-neutral-400"}`}>
            {formatTime(message.created_at)}
          </span>
          {isMine && <ReadIcon status={message.read_status} />}
        </div>
      </div>
    </div>
  );
}
